const roleDashboardConfigs = {
    superuser: {
        roleLabel: 'SuperUser',
        eyebrow: 'Executive control',
        heroTitle: 'SuperUser Command Dashboard',
        heroSummary: 'Monitor regional activity, warehouse health, shipment readiness, and data quality from one control point.',
        calloutTitle: 'Platform span',
        calloutText: 'This view is tuned for cross-functional oversight so major bottlenecks surface before they affect service delivery.',
        accentColor: '#0f766e',
        softColor: '#ccfbf1',
        highlights: [
            { label: 'Active regions', value: '12' },
            { label: 'Live alerts', value: '7' },
            { label: 'Data freshness', value: '96%' },
        ],
        statCards: [
            {
                icon: 'ShieldIcon', label: 'Role reviews', value: '24', note: 'Accounts pending approval',
            },
            {
                icon: 'TruckIcon', label: 'Shipment windows', value: '18', note: 'Scheduled for today',
            },
            {
                icon: 'ArchiveIcon', label: 'Warehouse load', value: '81%', note: 'Across all active sites',
            },
            {
                icon: 'DatabaseIcon', label: 'Processing SLA', value: '93%', note: 'Within target this week',
            },
        ],
        sections: [
            {
                type: 'focus-list',
                title: 'Governance Watchlist',
                subtitle: 'Items that need leadership attention this morning.',
                badge: 'Priority',
                badgeVariant: 'warning',
                colClass: 'lg-5',
                items: [
                    {
                        title: 'Dormant regional logins', meta: '14 user accounts have not checked in for 5 days', status: 'Needs review', variant: 'warning',
                    },
                    {
                        title: 'Backdated intake edits', meta: '3 markets submitted entries outside the allowed cycle', status: 'Escalate', variant: 'danger',
                    },
                    {
                        title: 'Pending role changes', meta: '6 staff members are waiting for updated permissions', status: 'Approve', variant: 'info',
                    },
                    {
                        title: 'Regional sync drift', meta: 'Northern cluster is 2 hours behind the target refresh time', status: 'Watch', variant: 'primary',
                    },
                ],
            },
            {
                type: 'progress-list',
                title: 'Enterprise Flow Health',
                subtitle: 'Live operational signals pulled into one leadership summary.',
                badge: 'Live',
                badgeVariant: 'success',
                colClass: 'lg-7',
                items: [
                    {
                        label: 'Regional data validation', value: 92, note: '2 regions need exception cleanup', variant: 'success',
                    },
                    {
                        label: 'Warehouse dispatch readiness', value: 84, note: 'Loading bays are almost balanced', variant: 'info',
                    },
                    {
                        label: 'Stores replenishment coverage', value: 68, note: 'Consumables list requires approval', variant: 'warning',
                    },
                    {
                        label: 'Processing queue completion', value: 89, note: 'On pace against weekly target', variant: 'primary',
                    },
                ],
            },
            {
                type: 'table',
                title: 'Regional Pulse',
                subtitle: 'Fast comparison of the most active zones.',
                colClass: 'lg-8',
                columns: ['Region', 'Records', 'Exceptions', 'Trend'],
                rows: [
                    ['Central', '14,220', '18', 'Up 4%'],
                    ['South', '12,980', '11', 'Stable'],
                    ['Lake', '10,540', '23', 'Needs review'],
                    ['West', '9,860', '8', 'Up 2%'],
                ],
            },
            {
                type: 'timeline',
                title: 'Control Room Updates',
                subtitle: 'Recent events that changed operational posture.',
                colClass: 'lg-4',
                items: [
                    {
                        time: '08:10', title: 'Warehouse sync finished', detail: 'All stock movements were reconciled across 6 facilities.', variant: 'success',
                    },
                    {
                        time: '08:45', title: 'Regional exception raised', detail: 'Lake region submitted duplicate bale records for validation.', variant: 'warning',
                    },
                    {
                        time: '09:05', title: 'Dispatch plan confirmed', detail: 'Shipping team locked the first outbound wave for 11:00.', variant: 'info',
                    },
                    {
                        time: '09:25', title: 'Role change requested', detail: 'Two processing supervisors were nominated for elevated access.', variant: 'primary',
                    },
                ],
            },
        ],
    },
    regionaldataofficer: {
        roleLabel: 'RegionalDataOfficer',
        eyebrow: 'Regional data command',
        heroTitle: 'Regional Data Officer Dashboard',
        heroSummary: 'Track field submissions, district validation quality, and unresolved anomalies across your assigned coverage area.',
        calloutTitle: 'Regional priority',
        calloutText: 'Use this view to keep district intake clean before records move into the core processing pipeline.',
        accentColor: '#1d4ed8',
        softColor: '#dbeafe',
        highlights: [
            { label: 'Districts monitored', value: '18' },
            { label: 'Pending validations', value: '46' },
            { label: 'Submission rate', value: '91%' },
        ],
        statCards: [
            {
                icon: 'MapPinIcon', label: 'Assigned regions', value: '3', note: 'Active operational coverage',
            },
            {
                icon: 'FileTextIcon', label: 'Fresh records', value: '2,840', note: 'Loaded since midnight',
            },
            {
                icon: 'AlertTriangleIcon', label: 'Open anomalies', value: '19', note: 'Need district follow-up',
            },
            {
                icon: 'UsersIcon', label: 'Field teams live', value: '27', note: 'Reporting this morning',
            },
        ],
        sections: [
            {
                type: 'action-grid',
                title: 'Today\'s Focus',
                subtitle: 'Practical next actions for regional oversight.',
                colClass: 'lg-4',
                items: [
                    { icon: 'ClipboardIcon', title: 'Review unverified records', detail: 'Prioritize district batches that missed mandatory fields.' },
                    { icon: 'PhoneCallIcon', title: 'Follow up with field clerks', detail: 'Resolve duplicate uploads before noon cut-off.' },
                    { icon: 'MapIcon', title: 'Check location coverage', detail: 'Confirm every assigned district has reported today.' },
                    { icon: 'SendIcon', title: 'Escalate blocked issues', detail: 'Push unresolved data mismatches to processing leadership.' },
                ],
            },
            {
                type: 'progress-list',
                title: 'Regional Quality Trackers',
                subtitle: 'Coverage and accuracy indicators by workflow stage.',
                badge: 'Live board',
                badgeVariant: 'info',
                colClass: 'lg-8',
                items: [
                    {
                        label: 'Daily district submissions', value: 88, note: '2 districts still pending upload', variant: 'primary',
                    },
                    {
                        label: 'Location code accuracy', value: 95, note: 'Most issues cleared during first pass', variant: 'success',
                    },
                    {
                        label: 'Rejected record rework', value: 63, note: 'Late corrections are building up', variant: 'warning',
                    },
                    {
                        label: 'Supervisor confirmations', value: 79, note: 'Waiting on 5 district sign-offs', variant: 'info',
                    },
                ],
            },
            {
                type: 'table',
                title: 'District Intake Snapshot',
                subtitle: 'Where the next round of attention is needed.',
                colClass: 'lg-6',
                columns: ['District', 'Received', 'Rejected', 'Status'],
                rows: [
                    ['Kongwa', '460', '6', 'Healthy'],
                    ['Mpwapwa', '395', '12', 'Review'],
                    ['Mvomero', '520', '4', 'Healthy'],
                    ['Kilosa', '410', '15', 'Escalate'],
                ],
            },
            {
                type: 'focus-list',
                title: 'Field Issues Requiring Escalation',
                subtitle: 'Operational blockers reported by district teams.',
                colClass: 'lg-6',
                items: [
                    {
                        title: 'Late village register upload', meta: 'Kilosa office missed the 07:30 sync window', status: 'High', variant: 'danger',
                    },
                    {
                        title: 'Duplicate grower references', meta: 'Mpwapwa batch 2 contains repeated farmer IDs', status: 'Medium', variant: 'warning',
                    },
                    {
                        title: 'Missing location hierarchy', meta: 'One ward was submitted without parent district mapping', status: 'High', variant: 'danger',
                    },
                    {
                        title: 'Team attendance gap', meta: 'Two clerks are offline in Kongwa due to connectivity', status: 'Watch', variant: 'info',
                    },
                ],
            },
        ],
    },
    shippingofficer: {
        roleLabel: 'ShippingOfficer',
        eyebrow: 'Dispatch center',
        heroTitle: 'Shipping Officer Dashboard',
        heroSummary: 'Keep outbound movement on schedule with a view of ready loads, documentation status, and transit hand-offs.',
        calloutTitle: 'Dispatch posture',
        calloutText: 'This layout emphasizes shipment sequencing, carrier readiness, and paperwork completion before release.',
        accentColor: '#c2410c',
        softColor: '#ffedd5',
        highlights: [
            { label: 'Loads queued', value: '11' },
            { label: 'Transit docs ready', value: '87%' },
            { label: 'On-time release', value: '94%' },
        ],
        statCards: [
            {
                icon: 'TruckIcon', label: 'Trucks on site', value: '8', note: 'Awaiting final release',
            },
            {
                icon: 'FileIcon', label: 'Signed manifests', value: '19', note: 'Prepared since shift start',
            },
            {
                icon: 'ClockIcon', label: 'Avg. dock delay', value: '14m', note: 'Below threshold',
            },
            {
                icon: 'CheckCircleIcon', label: 'Loads dispatched', value: '6', note: 'Completed this morning',
            },
        ],
        sections: [
            {
                type: 'status-grid',
                title: 'Dispatch Counters',
                subtitle: 'Immediate operational state for each critical checkpoint.',
                colClass: 'lg-4',
                items: [
                    {
                        icon: 'PackageIcon', label: 'Ready to load', value: '5', detail: 'Fully staged and verified',
                    },
                    {
                        icon: 'PenToolIcon', label: 'Docs pending', value: '3', detail: 'Carrier signatures outstanding',
                    },
                    {
                        icon: 'AlertCircleIcon', label: 'Route holds', value: '2', detail: 'Waiting for destination clearance',
                    },
                    {
                        icon: 'RefreshCcwIcon', label: 'Replanned loads', value: '1', detail: 'Sequence adjusted after weighbridge change',
                    },
                ],
            },
            {
                type: 'timeline',
                title: 'Shipment Movement Board',
                subtitle: 'Time-based hand-offs for the current dispatch wave.',
                badge: 'Shift log',
                badgeVariant: 'warning',
                colClass: 'lg-8',
                items: [
                    {
                        time: '07:50', title: 'Load GS-041 staged', detail: 'Warehouse 2 confirmed pallet count and seal check.', variant: 'success',
                    },
                    {
                        time: '08:20', title: 'Carrier document hold', detail: 'Truck TZH 482B is waiting for final destination stamp.', variant: 'warning',
                    },
                    {
                        time: '09:00', title: 'Outbound release approved', detail: 'Two long-haul consignments cleared for departure.', variant: 'info',
                    },
                    {
                        time: '09:30', title: 'Route plan updated', detail: 'Western lane dispatch was resequenced to avoid overlap.', variant: 'primary',
                    },
                ],
            },
            {
                type: 'table',
                title: 'Upcoming Loads',
                subtitle: 'Loads that need attention before final dispatch.',
                colClass: 'lg-12',
                columns: ['Load ID', 'Destination', 'Vehicle', 'Status'],
                rows: [
                    ['GS-042', 'Dar es Salaam', 'TZH 482B', 'Docs pending'],
                    ['GS-043', 'Morogoro', 'T 915 CAA', 'Ready to release'],
                    ['GS-044', 'Mwanza', 'T 602 DCM', 'Staging'],
                    ['GS-045', 'Dodoma', 'T 887 DSE', 'Awaiting seal'],
                ],
            },
        ],
    },
    dataclerk: {
        roleLabel: 'DataClerk',
        eyebrow: 'Entry desk',
        heroTitle: 'Data Clerk Dashboard',
        heroSummary: 'Stay focused on clean data capture, rework, and the next batch of records waiting for entry or correction.',
        calloutTitle: 'Clerk workflow',
        calloutText: 'This page keeps daily entry work narrow and practical so you can clear the queue with fewer validation misses.',
        accentColor: '#3f6212',
        softColor: '#ecfccb',
        highlights: [
            { label: 'Forms in queue', value: '62' },
            { label: 'Completed today', value: '145' },
            { label: 'Accuracy score', value: '97%' },
        ],
        statCards: [
            {
                icon: 'Edit3Icon', label: 'New entries', value: '84', note: 'Ready for capture',
            },
            {
                icon: 'RotateCcwIcon', label: 'Returned forms', value: '12', note: 'Need correction',
            },
            {
                icon: 'CheckSquareIcon', label: 'Verified records', value: '49', note: 'Passed first review',
            },
            {
                icon: 'ClockIcon', label: 'Avg. form age', value: '28m', note: 'Time in active queue',
            },
        ],
        sections: [
            {
                type: 'action-grid',
                title: 'Entry Checklist',
                subtitle: 'Keep these tasks moving to avoid a backlog.',
                colClass: 'lg-4',
                items: [
                    { icon: 'TypeIcon', title: 'Capture priority forms', detail: 'Start with records tagged for same-day processing.' },
                    { icon: 'CheckSquareIcon', title: 'Validate mandatory fields', detail: 'Reduce return rates before submission.' },
                    { icon: 'RefreshCwIcon', title: 'Correct rejected entries', detail: 'Clear the oldest rework batch first.' },
                    { icon: 'InboxIcon', title: 'Prepare next stack', detail: 'Arrange physical forms by source location.' },
                ],
            },
            {
                type: 'focus-list',
                title: 'Quality Attention List',
                subtitle: 'Frequent issues that slow down entry speed.',
                colClass: 'lg-4',
                items: [
                    {
                        title: 'Missing grower signatures', meta: '5 intake forms cannot move forward', status: 'Blocker', variant: 'danger',
                    },
                    {
                        title: 'Unreadable numeric fields', meta: '8 records need source confirmation', status: 'Review', variant: 'warning',
                    },
                    {
                        title: 'Duplicate receipt numbers', meta: '3 entries overlap with yesterday\'s batch', status: 'Check', variant: 'info',
                    },
                    {
                        title: 'Late corrections', meta: '4 returned forms are older than SLA', status: 'Urgent', variant: 'danger',
                    },
                ],
            },
            {
                type: 'timeline',
                title: 'Work Queue Timeline',
                subtitle: 'Recent progress across the current shift.',
                colClass: 'lg-4',
                items: [
                    {
                        time: '07:40', title: 'Morning batch assigned', detail: '32 forms routed to the intake desk.', variant: 'info',
                    },
                    {
                        time: '08:05', title: 'Validation pass completed', detail: '18 entries cleared without rework.', variant: 'success',
                    },
                    {
                        time: '08:55', title: 'Returned forms received', detail: '6 records came back from supervisor review.', variant: 'warning',
                    },
                    {
                        time: '09:15', title: 'Backlog reduced', detail: 'Queue age dropped below the half-hour target.', variant: 'primary',
                    },
                ],
            },
        ],
    },
    dataprocessingcoordinator: {
        roleLabel: 'DataProcessingCoordinator',
        eyebrow: 'Pipeline coordination',
        heroTitle: 'Data Processing Coordinator Dashboard',
        heroSummary: 'Balance intake, review, and output across the processing team while keeping hand-offs between stages stable.',
        calloutTitle: 'Coordination lens',
        calloutText: 'This view is structured around workload distribution, queue health, and blocked records waiting on cross-team action.',
        accentColor: '#0369a1',
        softColor: '#e0f2fe',
        highlights: [
            { label: 'Batches in flow', value: '21' },
            { label: 'Stage balance', value: '82%' },
            { label: 'Blocked items', value: '9' },
        ],
        statCards: [
            {
                icon: 'LayersIcon', label: 'Intake stage', value: '6', note: 'Batches waiting for review',
            },
            {
                icon: 'ShuffleIcon', label: 'Review stage', value: '8', note: 'Actively under validation',
            },
            {
                icon: 'DatabaseIcon', label: 'Output stage', value: '7', note: 'Ready for release downstream',
            },
            {
                icon: 'UsersIcon', label: 'Active processors', value: '13', note: 'Across the current shift',
            },
        ],
        sections: [
            {
                type: 'progress-list',
                title: 'Pipeline Throughput',
                subtitle: 'Measure stage completion and identify buildup early.',
                badge: 'Capacity',
                badgeVariant: 'primary',
                colClass: 'lg-7',
                items: [
                    {
                        label: 'Initial validation', value: 86, note: 'Running within capacity', variant: 'success',
                    },
                    {
                        label: 'Duplicate resolution', value: 58, note: 'Needs more attention from support clerks', variant: 'warning',
                    },
                    {
                        label: 'Supervisor approval', value: 77, note: 'Pending sign-offs on 4 batches', variant: 'info',
                    },
                    {
                        label: 'Release to reporting', value: 90, note: 'Downstream consumers are current', variant: 'primary',
                    },
                ],
            },
            {
                type: 'status-grid',
                title: 'Team Allocation',
                subtitle: 'A quick view of where staff time is currently assigned.',
                colClass: 'lg-5',
                items: [
                    {
                        icon: 'UsersIcon', label: 'Intake operators', value: '4', detail: 'Capturing new arrivals',
                    },
                    {
                        icon: 'SearchIcon', label: 'Review analysts', value: '5', detail: 'Resolving data quality checks',
                    },
                    {
                        icon: 'CheckCircleIcon', label: 'Approvers', value: '2', detail: 'Clearing final batch decisions',
                    },
                    {
                        icon: 'LifeBuoyIcon', label: 'Support pool', value: '2', detail: 'Handling escalated exceptions',
                    },
                ],
            },
            {
                type: 'table',
                title: 'Batch Flow Snapshot',
                subtitle: 'The batches most likely to affect today\'s completion target.',
                colClass: 'lg-12',
                columns: ['Batch', 'Stage', 'Owner', 'Risk'],
                rows: [
                    ['DP-118', 'Duplicate review', 'Analyst A', 'Medium'],
                    ['DP-119', 'Supervisor sign-off', 'Coordinator', 'Low'],
                    ['DP-120', 'Initial validation', 'Team B', 'Medium'],
                    ['DP-121', 'Release staging', 'Analyst C', 'Low'],
                ],
            },
        ],
    },
    externalusers: {
        roleLabel: 'ExternalUsers',
        eyebrow: 'Self-service portal',
        heroTitle: 'External Users Dashboard',
        heroSummary: 'Provide a clean entry point for shared documents, request tracking, and updates from the internal operations team.',
        calloutTitle: 'External experience',
        calloutText: 'This dashboard is intentionally simpler, with clear guidance on what has been shared and what still needs a response.',
        accentColor: '#475569',
        softColor: '#e2e8f0',
        highlights: [
            { label: 'Shared reports', value: '12' },
            { label: 'Requests open', value: '4' },
            { label: 'Responses on time', value: '98%' },
        ],
        statCards: [
            {
                icon: 'FileTextIcon', label: 'Latest documents', value: '7', note: 'Added this week',
            },
            {
                icon: 'MailIcon', label: 'Submitted requests', value: '9', note: 'Across current cycle',
            },
            {
                icon: 'CheckCircleIcon', label: 'Approved access', value: '3', note: 'Recently granted',
            },
            {
                icon: 'HelpCircleIcon', label: 'Support tickets', value: '2', note: 'Awaiting answer',
            },
        ],
        sections: [
            {
                type: 'action-grid',
                title: 'What You Can Do',
                subtitle: 'The most common activities for external account holders.',
                colClass: 'lg-4',
                items: [
                    { icon: 'DownloadIcon', title: 'Download approved reports', detail: 'Access documents that were shared with your account.' },
                    { icon: 'SendIcon', title: 'Submit a new request', detail: 'Ask for additional data extracts or document access.' },
                    { icon: 'ClockIcon', title: 'Track request progress', detail: 'See which requests are waiting for internal review.' },
                    { icon: 'BellIcon', title: 'Review announcements', detail: 'Stay current with release notes and portal notices.' },
                ],
            },
            {
                type: 'focus-list',
                title: 'Open Request Status',
                subtitle: 'Requests that still need internal action.',
                colClass: 'lg-4',
                items: [
                    {
                        title: 'Weekly warehouse summary', meta: 'Awaiting verification from reporting desk', status: 'In review', variant: 'info',
                    },
                    {
                        title: 'Shipment export pack', meta: 'Supporting manifest files are being compiled', status: 'Preparing', variant: 'primary',
                    },
                    {
                        title: 'Historic stock movement extract', meta: 'Need confirmation on period coverage', status: 'Action needed', variant: 'warning',
                    },
                    {
                        title: 'User access expansion', meta: 'Security review required before approval', status: 'Pending', variant: 'secondary',
                    },
                ],
            },
            {
                type: 'timeline',
                title: 'Portal Updates',
                subtitle: 'Recent activity visible to external users.',
                colClass: 'lg-4',
                items: [
                    {
                        time: 'Yesterday', title: 'New report pack shared', detail: 'April warehouse position files were published.', variant: 'success',
                    },
                    {
                        time: 'Yesterday', title: 'Request response posted', detail: 'Your shipment summary request was answered.', variant: 'info',
                    },
                    {
                        time: '2 days ago', title: 'Access review started', detail: 'Additional reviewer permissions entered security review.', variant: 'warning',
                    },
                    {
                        time: '3 days ago', title: 'Portal notice issued', detail: 'Planned maintenance window was announced to all users.', variant: 'primary',
                    },
                ],
            },
        ],
    },
    storesofficer: {
        roleLabel: 'StoresOfficer',
        eyebrow: 'Stores control',
        heroTitle: 'Stores Officer Dashboard',
        heroSummary: 'Manage material availability, replenishment timing, and issue transactions so operations never stall for stock reasons.',
        calloutTitle: 'Stores posture',
        calloutText: 'This view focuses on balance, reorder timing, and the stock items most likely to slow field or warehouse teams.',
        accentColor: '#047857',
        softColor: '#d1fae5',
        highlights: [
            { label: 'Items below reorder', value: '9' },
            { label: 'Issues today', value: '38' },
            { label: 'Stock accuracy', value: '95%' },
        ],
        statCards: [
            {
                icon: 'BoxIcon', label: 'Active SKUs', value: '148', note: 'Tracked in stores',
            },
            {
                icon: 'TrendingDownIcon', label: 'Fast movers', value: '16', note: 'Need close monitoring',
            },
            {
                icon: 'ShoppingBagIcon', label: 'Pending replenishment', value: '5', note: 'Awaiting approval',
            },
            {
                icon: 'ClipboardIcon', label: 'Issue vouchers', value: '22', note: 'Processed this shift',
            },
        ],
        sections: [
            {
                type: 'table',
                title: 'Stock Watchlist',
                subtitle: 'Items that need action before the next issue cycle.',
                badge: 'Critical',
                badgeVariant: 'danger',
                colClass: 'lg-7',
                columns: ['Item', 'On hand', 'Reorder point', 'Action'],
                rows: [
                    ['Printer labels', '120', '200', 'Reorder'],
                    ['Seal tapes', '34', '60', 'Expedite'],
                    ['Pallet tags', '420', '350', 'Healthy'],
                    ['Forms bundle', '18', '40', 'Replenish'],
                ],
            },
            {
                type: 'progress-list',
                title: 'Inventory Control Signals',
                subtitle: 'Core stores checks for the current cycle.',
                colClass: 'lg-5',
                items: [
                    {
                        label: 'Cycle count completion', value: 88, note: 'Most bays counted on schedule', variant: 'success',
                    },
                    {
                        label: 'Issue authorization rate', value: 79, note: 'Some vouchers still pending manager sign-off', variant: 'info',
                    },
                    {
                        label: 'Reorder readiness', value: 61, note: 'Low buffer on critical consumables', variant: 'warning',
                    },
                    {
                        label: 'Receiving confirmation', value: 93, note: 'Recent deliveries matched purchase records', variant: 'primary',
                    },
                ],
            },
            {
                type: 'focus-list',
                title: 'Stores Follow-up',
                subtitle: 'Tasks that require immediate attention from the stores desk.',
                colClass: 'lg-12',
                items: [
                    {
                        title: 'Approve seal tape replenishment', meta: 'Procurement request draft is ready for review', status: 'Today', variant: 'warning',
                    },
                    {
                        title: 'Resolve label count variance', meta: 'Cycle count differs from system balance by 24 units', status: 'Urgent', variant: 'danger',
                    },
                    {
                        title: 'Confirm incoming forms delivery', meta: 'Vendor truck is due before the next intake wave', status: 'Scheduled', variant: 'info',
                    },
                    {
                        title: 'Review aged issue vouchers', meta: '3 vouchers remain open after material collection', status: 'Check', variant: 'primary',
                    },
                ],
            },
        ],
    },
    warehouseoperationmanager: {
        roleLabel: 'WarehouseOperationManager',
        eyebrow: 'Warehouse command',
        heroTitle: 'Warehouse Operation Manager Dashboard',
        heroSummary: 'Balance space, dock flow, labor use, and dispatch readiness across the warehouse floor in real time.',
        calloutTitle: 'Floor visibility',
        calloutText: 'The sections below emphasize occupancy, load movement, and the operational friction points that affect throughput.',
        accentColor: '#b91c1c',
        softColor: '#fee2e2',
        highlights: [
            { label: 'Warehouse occupancy', value: '83%' },
            { label: 'Inbound today', value: '14' },
            { label: 'Outbound ready', value: '9' },
        ],
        statCards: [
            {
                icon: 'ArchiveIcon', label: 'Active stacks', value: '94', note: 'Across all zones',
            },
            {
                icon: 'TruckIcon', label: 'Dock movements', value: '23', note: 'Inbound and outbound combined',
            },
            {
                icon: 'UsersIcon', label: 'Shift crew', value: '31', note: 'Clocked in today',
            },
            {
                icon: 'AlertTriangleIcon', label: 'Blocked lanes', value: '2', note: 'Need floor intervention',
            },
        ],
        sections: [
            {
                type: 'status-grid',
                title: 'Warehouse Counters',
                subtitle: 'High-level state of the warehouse floor.',
                colClass: 'lg-4',
                items: [
                    {
                        icon: 'LogInIcon', label: 'Inbound bays', value: '4', detail: 'Currently receiving stock',
                    },
                    {
                        icon: 'LogOutIcon', label: 'Outbound bays', value: '3', detail: 'Loading dispatch-ready consignments',
                    },
                    {
                        icon: 'MapPinIcon', label: 'Congested zones', value: '2', detail: 'Need stack rearrangement',
                    },
                    {
                        icon: 'ToolIcon', label: 'Forklifts active', value: '6', detail: 'Operating across the shift',
                    },
                ],
            },
            {
                type: 'progress-list',
                title: 'Warehouse Flow Performance',
                subtitle: 'Measures that indicate whether floor movement is balanced.',
                colClass: 'lg-4',
                items: [
                    {
                        label: 'Inbound turnaround', value: 76, note: 'Receiving is slightly slower than target', variant: 'warning',
                    },
                    {
                        label: 'Put-away completion', value: 91, note: 'Most stock cleared within planned window', variant: 'success',
                    },
                    {
                        label: 'Pick accuracy', value: 95, note: 'Dispatch picks remain stable', variant: 'primary',
                    },
                    {
                        label: 'Stack utilization', value: 83, note: 'Re-slot lower volume product soon', variant: 'info',
                    },
                ],
            },
            {
                type: 'table',
                title: 'Zone Snapshot',
                subtitle: 'Fast operational readout by warehouse area.',
                colClass: 'lg-4',
                columns: ['Zone', 'Occupancy', 'Open task', 'Risk'],
                rows: [
                    ['A', '78%', 'Put-away', 'Low'],
                    ['B', '92%', 'Re-slot', 'Medium'],
                    ['C', '81%', 'Pick wave', 'Low'],
                    ['D', '86%', 'Lane clearing', 'Medium'],
                ],
            },
        ],
    },
    dataprocessingmanager: {
        roleLabel: 'DataProcessingManager',
        eyebrow: 'Management view',
        heroTitle: 'Data Processing Manager Dashboard',
        heroSummary: 'Manage throughput, approvals, and team performance with a leadership view of the processing organization.',
        calloutTitle: 'Manager focus',
        calloutText: 'This dashboard emphasizes decisions, not transactions, so you can rebalance staff and remove blockers quickly.',
        accentColor: '#1e3a8a',
        softColor: '#dbeafe',
        highlights: [
            { label: 'Team SLA', value: '94%' },
            { label: 'Approvals pending', value: '13' },
            { label: 'Weekly output', value: '4,820' },
        ],
        statCards: [
            {
                icon: 'BarChart2Icon', label: 'Processed today', value: '612', note: 'Across all teams',
            },
            {
                icon: 'UserCheckIcon', label: 'Active reviewers', value: '9', note: 'Working current queue',
            },
            {
                icon: 'CheckCircleIcon', label: 'Approved batches', value: '17', note: 'Released today',
            },
            {
                icon: 'AlertCircleIcon', label: 'Escalated blockers', value: '5', note: 'Require managerial action',
            },
        ],
        sections: [
            {
                type: 'focus-list',
                title: 'Management Decisions Queue',
                subtitle: 'The next set of decisions only the manager can clear.',
                badge: 'Approval queue',
                badgeVariant: 'primary',
                colClass: 'lg-4',
                items: [
                    {
                        title: 'Approve overtime request', meta: 'Evening shift requested 2 extra reviewers for backlog control', status: 'Today', variant: 'warning',
                    },
                    {
                        title: 'Clear policy exception batch', meta: 'One client upload requires manual override before release', status: 'Urgent', variant: 'danger',
                    },
                    {
                        title: 'Assign cross-team support', meta: 'Coordinator requested help for duplicate resolution', status: 'Review', variant: 'info',
                    },
                    {
                        title: 'Confirm weekly output note', meta: 'Leadership summary needs final sign-off', status: 'Pending', variant: 'secondary',
                    },
                ],
            },
            {
                type: 'progress-list',
                title: 'Leadership Performance Indicators',
                subtitle: 'Team-level performance indicators for today and this week.',
                colClass: 'lg-4',
                items: [
                    {
                        label: 'Daily completion target', value: 93, note: 'Slightly ahead of plan', variant: 'success',
                    },
                    {
                        label: 'Rework containment', value: 71, note: 'Returned items are climbing in one cluster', variant: 'warning',
                    },
                    {
                        label: 'Supervisor approval speed', value: 86, note: 'Approvals remain within policy time', variant: 'primary',
                    },
                    {
                        label: 'Escalation closure', value: 64, note: 'Blocked cases need faster resolution', variant: 'danger',
                    },
                ],
            },
            {
                type: 'table',
                title: 'Team Output Comparison',
                subtitle: 'A quick team-level performance comparison.',
                colClass: 'lg-4',
                columns: ['Team', 'Processed', 'Rework', 'Status'],
                rows: [
                    ['Team Alpha', '168', '9', 'Strong'],
                    ['Team Beta', '152', '14', 'Watch'],
                    ['Team Gamma', '141', '7', 'Strong'],
                    ['Team Delta', '151', '11', 'Stable'],
                ],
            },
            {
                type: 'timeline',
                title: 'Managerial Activity',
                subtitle: 'Key actions affecting the processing organization.',
                colClass: 'lg-12',
                items: [
                    {
                        time: '07:30', title: 'Shift target published', detail: 'Teams received the updated completion target for the day.', variant: 'info',
                    },
                    {
                        time: '08:25', title: 'High-risk batch escalated', detail: 'Policy exception batch routed for managerial review.', variant: 'warning',
                    },
                    {
                        time: '09:05', title: 'Support team reassigned', detail: 'Two analysts moved to duplicate resolution support.', variant: 'primary',
                    },
                    {
                        time: '09:40', title: 'Approval backlog reduced', detail: 'Five waiting batches were cleared for downstream reporting.', variant: 'success',
                    },
                ],
            },
        ],
    },
}

export const getRoleDashboardConfig = dashboardKey => roleDashboardConfigs[dashboardKey] || roleDashboardConfigs.superuser

export default roleDashboardConfigs
